import { Card, CardHeader, DataTable, EmptyState, PageHeader, SegmentedControl, Skeleton } from "@cafe/ui";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { day, http, money, monthLabel, pct } from "../lib/api";
import { LINE_KIND, PAY_METHOD } from "../lib/labels";
import { Bars, PeriodFilter, ShareRow, Tile, usePeriod } from "../lib/parts";

type Sales = {
  currency: string; from: string; to: string; group: "day" | "month";
  totals: {
    revenue: number; payments: number; companies: number; avgPayment: number; prevRevenue: number; growth: number | null; newPaying: number; newPayingRevenue: number;
    discount: number; invoicesPaid: number; setup: number; mrr: number; open: number; openCount: number; overdue: number; overdueCount: number;
  };
  series: { key: string; total: number; count: number }[];
  byKind: { kind: string; amount: number }[];
  items: { type: "plan" | "addon" | "other"; code: string; name: string; subscription: number; setup: number; proration: number; total: number; companies: number }[];
  methods: { method: string; total: number; count: number }[];
  topCompanies: { id: string; name: string; total: number; count: number }[];
};

/** Аналитика продаж подписок: сколько пришло денег за период, из чего сложилось, кто платит, сколько ждём. */
export default function SalesPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = usePeriod();
  const [group, setGroup] = useState<"day" | "month">("day");
  const q = useQuery({ queryKey: ["sales", period, group], queryFn: () => http.get<Sales>("/sales", { ...period, group }) });
  const d = q.data;
  const m = (v: number) => money(v, d?.currency);
  const kindsTotal = d?.byKind.reduce((a, k) => a + k.amount, 0) ?? 0;
  return (
    <>
      <PageHeader title="Продажи" description="Деньги, которые клиенты заплатили за подписку, дополнения и подключение. День оплаты — по времени Бишкека." />
      <PeriodFilter value={period} onChange={setPeriod} />
      {q.isPending ? <Skeleton className="h-96" /> : !d ? <EmptyState title="Нет данных" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Поступило" value={m(d.totals.revenue)} hint={`${pct(d.totals.growth)} к прошлому периоду (${m(d.totals.prevRevenue)})`} />
            <Tile label="Оплат" value={String(d.totals.payments)} hint={`Средняя оплата ${m(d.totals.avgPayment)} · платили ${d.totals.companies} компаний`} />
            <Tile label="Новые платящие" value={String(d.totals.newPaying)} hint={`Впервые заплатили ${m(d.totals.newPayingRevenue)}`} />
            <Tile label="За подключение" value={m(d.totals.setup)} hint="Разовая плата за подключение тарифов" />
            <Tile label="Доход в месяц сейчас" value={m(d.totals.mrr)} hint="Сколько платят все клиенты в пересчёте на месяц" />
            <Tile label="Скидки" value={m(d.totals.discount)} hint={`В ${d.totals.invoicesPaid} оплаченных счетах`} />
            <Tile label="Ждём оплату" value={m(d.totals.open)} hint={`Открытых счетов: ${d.totals.openCount}`} />
            <Tile label="Просрочено" value={m(d.totals.overdue)} tone={d.totals.overdue ? "danger" : undefined} hint={`Счетов после срока: ${d.totals.overdueCount}`} />
          </div>
          <Card>
            <CardHeader title="Поступления" description={`${day(d.from)} — ${day(d.to)}`}
              actions={<SegmentedControl aria-label="Шаг графика" size="sm" value={group} onValueChange={setGroup} options={[{ value: "day", label: "По дням" }, { value: "month", label: "По месяцам" }]} />} />
            <div className="px-4 pb-5 sm:px-5">
              <Bars label="Поступления по периодам" format={m}
                data={d.series.map((s) => ({ key: s.key, value: s.total, caption: `${d.group === "day" ? day(s.key) : monthLabel(s.key)} · оплат: ${s.count}` }))} />
            </div>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Из чего сложились продажи" description="Строки оплаченных счетов, до скидки" />
              <ul className="flex flex-col gap-3 px-5 pb-5">
                {d.byKind.length ? d.byKind.map((k) => <ShareRow key={k.kind} label={LINE_KIND[k.kind] ?? k.kind} value={k.amount} total={kindsTotal} format={m} />) : <EmptyState title="Оплат не было" />}
              </ul>
            </Card>
            <Card>
              <CardHeader title="Как платили" />
              <ul className="flex flex-col gap-3 px-5 pb-5">
                {d.methods.length ? d.methods.map((x) => <ShareRow key={x.method} label={`${PAY_METHOD[x.method] ?? x.method} · ${x.count}`} value={x.total} total={d.totals.revenue} format={m} />) : <EmptyState title="Оплат не было" />}
              </ul>
            </Card>
          </div>
          <Card>
            <CardHeader title="По тарифам и дополнениям" description="Подписка, разовая плата за подключение и доплаты при переходе" />
            <div className="px-2 pb-3">
              <DataTable rows={d.items} rowKey={(i) => `${i.type}:${i.code}`} empty={<EmptyState icon={<BarChart3 />} title="Оплат не было" />}
                columns={[
                  { key: "n", header: "Что", cell: (i) => <div><p className="font-medium">{i.name}</p><p className="text-xs text-muted">{i.type === "plan" ? "тариф" : i.type === "addon" ? "дополнение" : LINE_KIND[i.code] ?? "прочее"}</p></div>, mobile: "title" },
                  { key: "c", header: "Компаний", align: "end", cell: (i) => i.companies },
                  { key: "s", header: "Подписка", align: "end", cell: (i) => <span className="tabular-nums">{m(i.subscription)}</span> },
                  { key: "u", header: "Подключение", align: "end", cell: (i) => <span className="tabular-nums">{i.setup ? m(i.setup) : "—"}</span> },
                  { key: "p", header: "Доплаты", align: "end", cell: (i) => <span className="tabular-nums">{i.proration ? m(i.proration) : "—"}</span> },
                  { key: "t", header: "Итого", align: "end", cell: (i) => <span className="font-semibold tabular-nums">{m(i.total)}</span>, mobile: "subtitle" },
                ]} />
            </div>
          </Card>
          <Card>
            <CardHeader title="Кто заплатил больше всех" />
            <div className="px-2 pb-3">
              <DataTable rows={d.topCompanies} rowKey={(c) => c.id} onRowClick={(c) => navigate(`/companies/${c.id}`)} empty={<EmptyState title="Оплат не было" />}
                columns={[
                  { key: "n", header: "Компания", cell: (c) => c.name, mobile: "title" },
                  { key: "c", header: "Оплат", align: "end", cell: (c) => c.count },
                  { key: "t", header: "Сумма", align: "end", cell: (c) => <span className="font-semibold tabular-nums">{m(c.total)}</span>, mobile: "subtitle" },
                ]} />
            </div>
          </Card>
        </>
      )}
    </>
  );
}
